import { useEffect, useState, useCallback, useMemo } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";
import { Einklappbar, useBreite, BREAKPOINT_MITTEL } from "../ui/Bausteine";
import { zuletztGeoeffnet } from "../utils/zuletzt";

const RICHTUNGEN = [
  { value: "", label: "Alle" },
  { value: "einnahme", label: "Einnahme" },
  { value: "ausgabe", label: "Ausgabe" },
];

const KATEGORISIERT_OPTIONEN = [
  { value: "", label: "Alle" },
  { value: "true", label: "Kategorisiert" },
  { value: "false", label: "Nicht kategorisiert" },
];

const FINANZSTATUS_OPTIONEN = [
  { value: "betriebsrelevant", label: "Betriebsrelevant" },
  { value: "privat", label: "Privat" },
  { value: "neutral", label: "Neutral" },
  { value: "ignorieren", label: "Ignorieren" },
];
const FINANZSTATUS_FARBE = {
  betriebsrelevant: THEME.colors.success, privat: THEME.colors.accentPurple,
  neutral: THEME.colors.textMuted, ignorieren: THEME.colors.danger,
};
const FINANZSTATUS_LABEL = Object.fromEntries(FINANZSTATUS_OPTIONEN.map(o => [o.value, o.label]));

const VERKUERZE = (text, laenge = 55) => (!text ? "" : text.length > laenge ? text.slice(0, laenge) + "…" : text);

function heuteISO(d) {
  // Lokales Datum statt toISOString() (UTC): sonst rutscht der 1. eines Monats (lokal 00:00) in der
  // Sommerzeit auf den 31. des Vormonats - "Letzter Monat" enthielt dadurch faelschlich den Vormonatsletzten.
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function zeitraumFuerPreset(preset) {
  const heute = new Date();
  if (preset === "aktueller_monat") {
    return { von: heuteISO(new Date(heute.getFullYear(), heute.getMonth(), 1)), bis: heuteISO(heute) };
  }
  if (preset === "letzter_monat") {
    const ersterDiesen = new Date(heute.getFullYear(), heute.getMonth(), 1);
    const letzterVormonat = new Date(ersterDiesen - 1);
    const ersterVormonat = new Date(letzterVormonat.getFullYear(), letzterVormonat.getMonth(), 1);
    return { von: heuteISO(ersterVormonat), bis: heuteISO(letzterVormonat) };
  }
  return { von: "", bis: "" };
}

export default function BankumsaetzeSeite({ benutzerId, initialFilter }) {
  const breite = useBreite();
  const kompakt = breite < BREAKPOINT_MITTEL;      // Smartphone/Tablet: Karten statt Tabelle, einklappbare Filter
  const [eintraege, setEintraege] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [importe, setImporte] = useState([]);
  const [gesamt, setGesamt] = useState(0);
  const [seite, setSeite] = useState(1);
  const [seitenGesamt, setSeitenGesamt] = useState(1);
  const [seitenGroesse, setSeitenGroesse] = useState(typeof window !== "undefined" && window.innerWidth < BREAKPOINT_MITTEL ? 10 : 25);   // Smartphone: kurze Seiten
  const [zeitraumPreset, setZeitraumPreset] = useState("alle");
  // initialFilter kommt von der Uebersicht ("Jetzt bearbeiten"-Links bei Offenen Aufgaben) -
  // kategorisiert ist dort ein Boolean, das <select> erwartet aber die String-Werte "true"/"false".
  const [filter, setFilter] = useState({
    richtung: "", suche: "", von: "", bis: "", kategorisiert: "", verarbeitungs_status: "", finanzstatus: "", import_id: "",
    ...(initialFilter ? { ...initialFilter, kategorisiert: initialFilter.kategorisiert !== undefined ? String(initialFilter.kategorisiert) : "" } : {}),
  });
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [ausgewaehlt, setAusgewaehlt] = useState(new Set());

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler("");
    try {
      const daten = await finanzenApi.bankumsaetzeListe(benutzerId, { ...filter, seite, seiten_groesse: seitenGroesse === "alle" ? 5000 : seitenGroesse });
      setEintraege(daten.eintraege);
      setGesamt(daten.gesamt);
      setSeitenGesamt(daten.seiten_gesamt);
      setAusgewaehlt(new Set());
    } catch (e) {
      setFehler(e.message);
    }
    setLaedt(false);
  }, [benutzerId, filter, seite, seitenGroesse]);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => { finanzenApi.kategorienListe(benutzerId).then(setKategorien).catch(() => {}); }, [benutzerId]);
  useEffect(() => { finanzenApi.importListe(benutzerId, { seiten_groesse: 50 }).then(d => setImporte(d.eintraege)).catch(() => {}); }, [benutzerId]);

  const filterAendern = (feld, wert) => { setSeite(1); setFilter(f => ({ ...f, [feld]: wert })); };
  const zeitraumAendern = (preset) => {
    setZeitraumPreset(preset);
    if (preset !== "eigener") {
      const { von, bis } = zeitraumFuerPreset(preset);
      setSeite(1);
      setFilter(f => ({ ...f, von, bis }));
    }
  };

  const alleAufSeiteAusgewaehlt = eintraege.length > 0 && eintraege.every(e => ausgewaehlt.has(e.id));
  const toggleAlle = () => {
    setAusgewaehlt(prev => {
      if (alleAufSeiteAusgewaehlt) return new Set();
      return new Set(eintraege.map(e => e.id));
    });
  };
  const toggleEins = (id) => {
    setAusgewaehlt(prev => {
      const neu = new Set(prev);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
  };

  const regelVorschlagen = async (umsatz, kategorieId, finanzstatus) => {
    if (!umsatz.zahlungspartner_name) return;
    const kat = kategorieId ? kategorien.find(k => k.id === Number(kategorieId)) : null;
    const teile = [];
    if (kat) teile.push(`Kategorie „${kat.name}"`);
    if (finanzstatus) teile.push(`Finanzstatus „${FINANZSTATUS_LABEL[finanzstatus]}"`);
    if (teile.length === 0) return;
    if (window.confirm(`Diese Zuordnung künftig automatisch anwenden?\n\nAlle künftigen Buchungen von „${umsatz.zahlungspartner_name}" würden dann automatisch ${teile.join(" und ")} erhalten.`)) {
      await finanzenApi.regelErstellen(benutzerId, {
        name: `Auto: ${umsatz.zahlungspartner_name}`, prioritaet: 0, feld: "zahlungspartner",
        operator: "entspricht", vergleichswert: umsatz.zahlungspartner_name,
        kategorie_id: kategorieId ? Number(kategorieId) : null,
        setzt_finanzstatus: finanzstatus || null,
      });
    }
  };

  const kategorieInlineAendern = async (umsatz, neueKategorieId) => {
    try {
      await finanzenApi.bankumsatzBearbeiten(benutzerId, umsatz.id, { kategorie_id: neueKategorieId || null });
      if (neueKategorieId) await regelVorschlagen(umsatz, neueKategorieId, null);
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const finanzstatusInlineAendern = async (umsatz, neuerStatus) => {
    try {
      await finanzenApi.bankumsatzBearbeiten(benutzerId, umsatz.id, { finanzstatus: neuerStatus });
      await regelVorschlagen(umsatz, null, neuerStatus);
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const alsGeprueftMarkieren = async (umsatz) => {
    try {
      await finanzenApi.bankumsatzBearbeiten(benutzerId, umsatz.id, { verarbeitungs_status: "verarbeitet" });
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const sammelaktion = async (daten, label) => {
    if (ausgewaehlt.size === 0) return;
    if (!window.confirm(`${label} für ${ausgewaehlt.size} ausgewählte Buchungen anwenden?`)) return;
    try {
      await finanzenApi.bankumsaetzeBulkBearbeiten(benutzerId, { ids: Array.from(ausgewaehlt), ...daten });
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const sammelNotiz = async () => {
    if (ausgewaehlt.size === 0) return;
    const notiz = window.prompt(`Notiz für ${ausgewaehlt.size} ausgewählte Buchungen:`);
    if (notiz === null || notiz.trim() === "") return;
    try {
      await finanzenApi.bankumsaetzeBulkBearbeiten(benutzerId, { ids: Array.from(ausgewaehlt), notiz });
      laden();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const sammelKategorie = async () => {
    if (ausgewaehlt.size === 0 || kategorien.length === 0) return;
    const namen = kategorien.map((k, i) => `${i + 1}: ${k.name} (${k.kategorie_typ})`).join("\n");
    const eingabe = window.prompt(`Kategorie-Nummer für ${ausgewaehlt.size} ausgewählte Buchungen:\n\n${namen}`);
    const index = Number(eingabe) - 1;
    if (!eingabe || index < 0 || index >= kategorien.length) return;
    await sammelaktion({ kategorie_id: kategorien[index].id }, `Kategorie „${kategorien[index].name}"`);
  };

  const detailOeffnen = (u) => {
    zuletztGeoeffnet.merken({ typ: "bankumsatz", id: u.id, titel: `${u.buchungsdatum} · ${u.betrag.toFixed(2)} €`, untertitel: u.zahlungspartner_name || "–", suche: u.zahlungspartner_name || "" });
    setDetailId(u.id);
  };
  const aktiveFilter = ["richtung", "kategorie_id", "kategorisiert", "finanzstatus", "verarbeitungs_status", "import_id", "suche"].filter(k => filter[k]).length + (zeitraumPreset !== "alle" ? 1 : 0);

  const kategorieOptionen = useMemo(() => kategorien.map(k => ({ value: k.id, label: k.name })), [kategorien]);

  return (
    <div>
      <KontostandWidget benutzerId={benutzerId} />
      <FilterRahmen kompakt={kompakt} aktiv={aktiveFilter}>
        <div>
          <label style={fs.label}>Zeitraum</label>
          <select style={fs.input} value={zeitraumPreset} onChange={e => zeitraumAendern(e.target.value)}>
            <option value="alle">Alle</option>
            <option value="aktueller_monat">Aktueller Monat</option>
            <option value="letzter_monat">Letzter Monat</option>
            <option value="eigener">Eigener Zeitraum</option>
          </select>
        </div>
        {zeitraumPreset === "eigener" && (
          <>
            <div>
              <label style={fs.label}>Von</label>
              <input type="date" style={fs.input} value={filter.von} onChange={e => filterAendern("von", e.target.value)} />
            </div>
            <div>
              <label style={fs.label}>Bis</label>
              <input type="date" style={fs.input} value={filter.bis} onChange={e => filterAendern("bis", e.target.value)} />
            </div>
          </>
        )}
        <div>
          <label style={fs.label}>Richtung</label>
          <select style={fs.input} value={filter.richtung} onChange={e => filterAendern("richtung", e.target.value)}>
            {RICHTUNGEN.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label style={fs.label}>Kategorie</label>
          <select style={fs.input} value={filter.kategorie_id || ""} onChange={e => filterAendern("kategorie_id", e.target.value)}>
            <option value="">Alle</option>
            {kategorieOptionen.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>
        <div>
          <label style={fs.label}>Kategorisierung</label>
          <select style={fs.input} value={filter.kategorisiert} onChange={e => filterAendern("kategorisiert", e.target.value)}>
            {KATEGORISIERT_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={fs.label}>Finanzstatus</label>
          <select style={fs.input} value={filter.finanzstatus} onChange={e => filterAendern("finanzstatus", e.target.value)}>
            <option value="">Alle</option>
            {FINANZSTATUS_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={fs.label}>Status</label>
          <select style={fs.input} value={filter.verarbeitungs_status} onChange={e => filterAendern("verarbeitungs_status", e.target.value)}>
            <option value="">Alle</option>
            <option value="pruefung_erforderlich">Prüfung erforderlich</option>
          </select>
        </div>
        <div>
          <label style={fs.label}>Import</label>
          <select style={fs.input} value={filter.import_id} onChange={e => filterAendern("import_id", e.target.value)}>
            <option value="">Alle</option>
            {importe.map(i => <option key={i.id} value={i.id}>{i.dateiname} ({i.importiert_am})</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 220, gridColumn: "1 / -1" }}>
          <label style={fs.label}>Suche (Partner, Zweck, IBAN, Betrag, Referenzen)</label>
          <input style={{ ...fs.input, width: "100%" }} value={filter.suche} onChange={e => filterAendern("suche", e.target.value)} />
        </div>
      </FilterRahmen>

      {kompakt && eintraege.length > 0 && (
        <label style={{ display: "flex", gap: 10, alignItems: "center", minHeight: 48, color: THEME.colors.textSecondary }}>
          <input type="checkbox" style={{ width: 22, height: 22 }} checked={alleAufSeiteAusgewaehlt} onChange={toggleAlle} /> Alle auf dieser Seite auswählen
        </label>
      )}
      {ausgewaehlt.size > 0 && (
        <div style={{ ...fs.karte, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: THEME.colors.surfaceMuted }}>
          <strong style={{ color: THEME.colors.text }}>{ausgewaehlt.size} ausgewählt</strong>
          <button style={fs.btnGray} onClick={sammelKategorie}>Kategorie zuweisen</button>
          <button style={fs.btnGray} onClick={() => sammelaktion({ finanzstatus: "betriebsrelevant" }, "Als betrieblich markieren")}>Betrieblich</button>
          <button style={fs.btnGray} onClick={() => sammelaktion({ finanzstatus: "privat" }, "Als privat markieren")}>Privat</button>
          <button style={fs.btnGray} onClick={() => sammelaktion({ finanzstatus: "neutral" }, "Als neutral markieren")}>Neutral</button>
          <button style={fs.btnGray} onClick={() => sammelaktion({ verarbeitungs_status: "verarbeitet" }, "Als geprüft markieren")}>✓ Als geprüft markieren</button>
          <button style={fs.btnGray} onClick={sammelNotiz}>Notiz hinzufügen</button>
          <button style={fs.btnDanger} onClick={() => sammelaktion({ finanzstatus: "ignorieren" }, "Ignorieren")}>Ignorieren</button>
          <button style={{ ...fs.btnGray, marginLeft: "auto" }} onClick={() => setAusgewaehlt(new Set())}>Auswahl aufheben</button>
        </div>
      )}

      {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}

      {kompakt && (
        <div style={{ display: "grid", gap: 10 }} aria-label="Bankumsätze">
          {eintraege.map(u => (
            <UmsatzKarte key={u.id} u={u} gewaehlt={ausgewaehlt.has(u.id)} onWahl={() => toggleEins(u.id)} kategorieOptionen={kategorieOptionen}
              onKategorie={(v) => kategorieInlineAendern(u, v)} onFinanzstatus={(v) => finanzstatusInlineAendern(u, v)} onGeprueft={() => alsGeprueftMarkieren(u)} onDetails={() => detailOeffnen(u)} />
          ))}
          {!laedt && eintraege.length === 0 && <div style={{ ...fs.karte, margin: 0, color: THEME.colors.textMuted }}>Keine Bankumsätze gefunden.</div>}
        </div>
      )}
      {!kompakt && <div style={fs.tabelle}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={fs.th}><input type="checkbox" checked={alleAufSeiteAusgewaehlt} onChange={toggleAlle} /></th>
              <th style={fs.th}>Datum</th>
              <th style={fs.th}>Zahlungspartner</th>
              <th style={fs.th}>Verwendungszweck</th>
              <th style={{ ...fs.th, textAlign: "right" }}>Betrag</th>
              <th style={fs.th}>Kategorie</th>
              <th style={fs.th}>Finanzstatus</th>
              <th style={fs.th}>Status</th>
              <th style={fs.th}>Notiz</th>
              <th style={fs.th}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {eintraege.map(u => (
              <tr key={u.id} style={{ opacity: u.finanzstatus === "ignorieren" ? 0.5 : 1 }}>
                <td style={fs.td} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={ausgewaehlt.has(u.id)} onChange={() => toggleEins(u.id)} />
                </td>
                <td style={fs.td}>{u.buchungsdatum}</td>
                <td style={fs.td}>{u.zahlungspartner_name || "–"}</td>
                <td style={fs.td} title={u.verwendungszweck || ""}>{VERKUERZE(u.verwendungszweck)}</td>
                <td style={{ ...fs.td, textAlign: "right", color: u.richtung === "einnahme" ? THEME.colors.success : THEME.colors.danger, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {u.betrag > 0 ? "+" : ""}{u.betrag.toFixed(2)} €
                </td>
                <td style={fs.td} onClick={e => e.stopPropagation()}>
                  <select style={{ ...fs.input, padding: "6px 8px", fontSize: THEME.fontSize.sm }} value={u.kategorie_id || ""}
                    onChange={e => kategorieInlineAendern(u, e.target.value)}>
                    <option value="">– keine –</option>
                    {kategorieOptionen.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </td>
                <td style={fs.td} onClick={e => e.stopPropagation()}>
                  <select style={{ ...fs.input, padding: "6px 8px", fontSize: THEME.fontSize.sm, color: FINANZSTATUS_FARBE[u.finanzstatus] }}
                    value={u.finanzstatus} onChange={e => finanzstatusInlineAendern(u, e.target.value)}>
                    {FINANZSTATUS_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td style={fs.td} onClick={e => e.stopPropagation()}>
                  {u.verarbeitungs_status === "pruefung_erforderlich"
                    ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ color: THEME.colors.warning }}>⚠ Prüfung</span>
                        <button style={{ ...fs.btnGray, padding: "2px 8px", fontSize: THEME.fontSize.xs }} onClick={() => alsGeprueftMarkieren(u)} title="Als geprüft markieren, z.B. nachdem Kategorie/Finanzstatus gesetzt wurden">✓ Erledigt</button>
                      </span>
                    )
                    : <span style={{ color: THEME.colors.textMuted }}>{u.zuordnungs_status}</span>}
                </td>
                <td style={fs.td} title={u.notiz || ""}>{u.notiz ? "📝" : ""}</td>
                <td style={fs.td}>
                  <button style={{ ...fs.btnGray, padding: "4px 10px", fontSize: THEME.fontSize.sm }} onClick={() => detailOeffnen(u)}>Details</button>
                </td>
              </tr>
            ))}
            {!laedt && eintraege.length === 0 && (
              <tr><td style={fs.td} colSpan={10}>Keine Bankumsätze gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{gesamt} Bankumsätze</span>
          <select style={{ ...fs.input, padding: "4px 8px", fontSize: THEME.fontSize.sm, width: "auto" }}
            value={seitenGroesse} onChange={e => { setSeitenGroesse(e.target.value === "alle" ? "alle" : Number(e.target.value)); setSeite(1); }}>
            {[...(kompakt ? [10] : []), 25, 50, 100].map(n => <option key={n} value={n}>{n} pro Seite</option>)}
            {!kompakt && <option value="alle">Alle</option>}
          </select>
        </div>
        {seitenGroesse !== "alle" && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button style={{ ...fs.btnGray, opacity: seite <= 1 ? 0.5 : 1 }} disabled={seite <= 1} onClick={() => setSeite(s => s - 1)}>← Zurück</button>
            <span style={{ color: THEME.colors.textMuted, alignSelf: "center" }}>Seite {seite} / {seitenGesamt}</span>
            <button style={{ ...fs.btnGray, opacity: seite >= seitenGesamt ? 0.5 : 1 }} disabled={seite >= seitenGesamt} onClick={() => setSeite(s => s + 1)}>Weiter →</button>
          </div>
        )}
      </div>

      {detailId && (
        <BankumsatzDetail benutzerId={benutzerId} umsatzId={detailId} kategorien={kategorien}
          onSchliessen={() => setDetailId(null)} onGespeichert={() => { setDetailId(null); laden(); }} />
      )}
    </div>
  );
}

// Filter: auf dem Desktop wie bisher als Karte; auf Smartphone/Tablet eingeklappt (mit Anzahl aktiver Filter)
function FilterRahmen({ kompakt, aktiv, children }) {
  if (!kompakt) return <div style={{ ...fs.karte, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>{children}</div>;
  return (
    <Einklappbar titel="Filter" zusatz={aktiv ? `${aktiv} aktiv` : "keine aktiv"}>
      <div className="fm-filter" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, alignItems: "end" }}>{children}</div>
    </Einklappbar>
  );
}

// Eine Buchung als Karte (Smartphone): Datum, Partner, Verwendungszweck, Betrag, Kategorie, Zuordnungsstatus und direkte Bearbeitung
function UmsatzKarte({ u, gewaehlt, onWahl, kategorieOptionen, onKategorie, onFinanzstatus, onGeprueft, onDetails }) {
  const pruefung = u.verarbeitungs_status === "pruefung_erforderlich";
  const farbe = u.richtung === "einnahme" ? THEME.colors.success : THEME.colors.danger;
  return (
    <article style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: "12px 14px", opacity: u.finanzstatus === "ignorieren" ? 0.55 : 1, border: `1px solid ${gewaehlt ? THEME.colors.primary : THEME.colors.border}`, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 48, color: THEME.colors.textSecondary }}>
          <input type="checkbox" style={{ width: 22, height: 22 }} checked={gewaehlt} onChange={onWahl} aria-label={`Buchung vom ${u.buchungsdatum} auswählen`} />
          {u.buchungsdatum}
        </label>
        <strong style={{ color: farbe, fontSize: THEME.fontSize.lg, whiteSpace: "nowrap" }}>{u.betrag > 0 ? "+" : ""}{u.betrag.toFixed(2)} €</strong>
      </div>
      <div style={{ color: THEME.colors.text, fontWeight: 600, overflowWrap: "anywhere" }}>{u.zahlungspartner_name || "–"}</div>
      {u.verwendungszweck && <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere", marginTop: 2 }}>{VERKUERZE(u.verwendungszweck, 120)}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "10px 0" }}>
        {pruefung
          ? <><span style={{ color: THEME.colors.warning }}>⚠ Prüfung erforderlich</span><button style={{ ...fs.btnGray, minHeight: 48 }} onClick={onGeprueft}>✓ Erledigt</button></>
          : <span style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: 999, padding: "3px 12px", fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>{u.zuordnungs_status || "offen"}</span>}
        {u.notiz && <span title={u.notiz} aria-label="Notiz vorhanden">📝</span>}
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <select aria-label="Kategorie" style={{ ...fs.input, width: "100%", minWidth: 0, padding: "10px 8px" }} value={u.kategorie_id || ""} onChange={e => onKategorie(e.target.value)}>
            <option value="">Keine Kategorie</option>
            {kategorieOptionen.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <select aria-label="Finanzstatus" style={{ ...fs.input, width: "100%", minWidth: 0, padding: "10px 8px", color: FINANZSTATUS_FARBE[u.finanzstatus] }} value={u.finanzstatus} onChange={e => onFinanzstatus(e.target.value)}>
            {FINANZSTATUS_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button style={{ ...fs.btnGray, minHeight: 48, width: "100%" }} onClick={onDetails}>Details und Notiz bearbeiten</button>
      </div>
    </article>
  );
}

function heuteISODatum() { return heuteISO(new Date()); }

function KontostandWidget({ benutzerId }) {
  const [datum, setDatum] = useState(heuteISODatum());
  const [ergebnis, setErgebnis] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState("");

  const abfragen = useCallback(async (d) => {
    setLaedt(true);
    setFehler("");
    try {
      setErgebnis(await finanzenApi.kontostand(benutzerId, d));
    } catch (e) {
      setFehler(e.message);
      setErgebnis(null);
    }
    setLaedt(false);
  }, [benutzerId]);

  useEffect(() => { abfragen(datum); }, [abfragen, datum]);

  return (
    <div style={{ ...fs.karte, display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div>
        <label style={fs.label}>Kontostand zum</label>
        <input type="date" style={fs.input} value={datum} onChange={e => setDatum(e.target.value)} />
      </div>
      <div style={{ minWidth: 200 }}>
        {laedt && <span style={{ color: THEME.colors.textMuted }}>Lädt…</span>}
        {!laedt && fehler && <span style={{ color: THEME.colors.danger }}>{fehler}</span>}
        {!laedt && !fehler && ergebnis && ergebnis.kontostand != null && (
          <>
            <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "0 0 2px" }}>
              Kontostand am {ergebnis.stand_vom}
            </p>
            <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.xl, fontWeight: 700, margin: 0 }}>
              {ergebnis.kontostand.toFixed(2)} €
            </p>
          </>
        )}
        {!laedt && !fehler && ergebnis && ergebnis.kontostand == null && (
          <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>
            Kein Kontostand verfügbar (kein Bankumsatz mit Saldo-Angabe an oder vor diesem Datum importiert).
          </span>
        )}
      </div>
    </div>
  );
}

function BankumsatzDetail({ benutzerId, umsatzId, kategorien, onSchliessen, onGespeichert }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState("");
  // "Mit Rechnung verknuepfen": Nutzer-Wunsch 2026-09-04 - eine eingehende Zahlung (z.B. Rolf
  // Weinert 119,90€) soll sich direkt hier mit einer bestehenden offenen Rechnung verknuepfen und
  // diese dabei als bezahlt markieren lassen, statt separat unter Rechnungen nachschlagen zu
  // muessen. Nutzt das bisher ungenutzte Bankumsatz.rechnung_id-Feld erstmals wirklich.
  const [offeneRechnungen, setOffeneRechnungen] = useState(null);
  const [rechnungSuche, setRechnungSuche] = useState("");
  const [verknuepfeLaedt, setVerknuepfeLaedt] = useState(false);

  useEffect(() => {
    finanzenApi.bankumsatzDetail(benutzerId, umsatzId)
      .then(d => {
        setDetail(d);
        setForm({ kategorie_id: d.kategorie_id, finanzstatus: d.finanzstatus, notiz: d.notiz || "" });
        if (d.betrag > 0 && !d.rechnung_id) {
          finanzenApi.rechnungenListe().then(alle => setOffeneRechnungen(alle.filter(r => !r.bezahlt))).catch(() => setOffeneRechnungen([]));
        }
      })
      .catch(e => setFehler(e.message));
  }, [benutzerId, umsatzId]);

  const speichern = async () => {
    setSpeichert(true);
    setFehler("");
    try {
      await finanzenApi.bankumsatzBearbeiten(benutzerId, umsatzId, form);
      onGespeichert();
    } catch (e) {
      setFehler(e.message);
    }
    setSpeichert(false);
  };

  const rechnungVerknuepfen = async (rechnungId) => {
    setVerknuepfeLaedt(true);
    setFehler("");
    try {
      await finanzenApi.bankumsatzRechnungVerknuepfen(benutzerId, umsatzId, rechnungId);
      onGespeichert();
    } catch (e) {
      setFehler(e.message);
    }
    setVerknuepfeLaedt(false);
  };

  const suchtext = rechnungSuche.trim().toLowerCase();
  const gefilterteRechnungen = (offeneRechnungen || []).filter(r =>
    !suchtext || (r.empfaenger_name || "").toLowerCase().includes(suchtext) || (r.nummer || "").toLowerCase().includes(suchtext)
  );

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
      onClick={onSchliessen}>
      <div style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xxxl, padding: "2rem", width: "min(560px, 95vw)", maxHeight: "90vh", overflowY: "auto" }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ color: THEME.colors.text, marginTop: 0 }}>Bankumsatz-Details</h3>
        {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}
        {!detail || !form ? <p style={{ color: THEME.colors.textMuted }}>Lädt…</p> : (
          <>
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>Datum: <span style={{ color: THEME.colors.text }}>{detail.buchungsdatum}</span></p>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>Betrag: <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{detail.betrag.toFixed(2)} €</span></p>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>Zahlungspartner: <span style={{ color: THEME.colors.text }}>{detail.zahlungspartner_name || "–"}</span></p>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>IBAN: <span style={{ color: THEME.colors.text }}>{detail.zahlungspartner_iban || "–"}</span></p>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>Buchungstext: <span style={{ color: THEME.colors.text }}>{detail.buchungstext || "–"}</span></p>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>Mandatsreferenz: <span style={{ color: THEME.colors.text }}>{detail.mandatsreferenz || "–"}</span></p>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>End-to-End-Referenz: <span style={{ color: THEME.colors.text }}>{detail.e2e_referenz || "–"}</span></p>
              <p style={{ color: THEME.colors.textMuted, margin: "4px 0" }}>Verwendungszweck (vollständig): <span style={{ color: THEME.colors.text, whiteSpace: "pre-wrap" }}>{detail.verwendungszweck || "–"}</span></p>
            </div>
            {detail.rechnung_id && (
              <div style={{ background: "#22c55e22", border: `1px solid ${THEME.colors.success}`, borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.success, fontWeight: 600, margin: 0 }}>✓ Mit Rechnung verknüpft, als bezahlt markiert.</p>
              </div>
            )}
            {offeneRechnungen !== null && offeneRechnungen.length > 0 && !detail.rechnung_id && (
              <div style={{ background: THEME.colors.background, borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <p style={{ color: THEME.colors.text, fontWeight: 600, margin: "0 0 8px" }}>Mit offener Rechnung verknüpfen &amp; als bezahlt markieren</p>
                <input style={{ ...fs.input, width: "100%", marginBottom: 8 }} placeholder="Name oder Rechnungsnummer suchen…"
                  value={rechnungSuche} onChange={e => setRechnungSuche(e.target.value)} />
                <div style={{ maxHeight: 160, overflowY: "auto" }}>
                  {gefilterteRechnungen.length === 0 && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Keine passende offene Rechnung gefunden.</p>}
                  {gefilterteRechnungen.map(r => (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: "1px solid #2b3d52" }}>
                      <span style={{ color: THEME.colors.text, fontSize: THEME.fontSize.sm }}>{r.nummer} – {r.empfaenger_name} ({parseFloat(r.gesamt).toFixed(2)} €)</span>
                      <button style={{ ...fs.btn, width: "auto", padding: "4px 10px", fontSize: THEME.fontSize.sm }} disabled={verknuepfeLaedt}
                        onClick={() => rechnungVerknuepfen(r.id)}>{verknuepfeLaedt ? "…" : "Verknüpfen"}</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <label style={fs.label}>Kategorie</label>
            <select style={{ ...fs.input, width: "100%", marginBottom: 16 }} value={form.kategorie_id || ""}
              onChange={e => setForm(f => ({ ...f, kategorie_id: e.target.value ? Number(e.target.value) : null }))}>
              <option value="">– keine –</option>
              {kategorien.map(k => <option key={k.id} value={k.id}>{k.name} ({k.kategorie_typ})</option>)}
            </select>
            <label style={fs.label}>Finanzstatus</label>
            <select style={{ ...fs.input, width: "100%", marginBottom: 16, color: FINANZSTATUS_FARBE[form.finanzstatus] }}
              value={form.finanzstatus} onChange={e => setForm(f => ({ ...f, finanzstatus: e.target.value }))}>
              {FINANZSTATUS_OPTIONEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <label style={fs.label}>Notiz</label>
            <textarea style={{ ...fs.input, width: "100%", minHeight: 70 }} value={form.notiz} onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))} />
            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              <button style={fs.btn} onClick={speichern} disabled={speichert}>{speichert ? "Speichert…" : "Speichern"}</button>
              <button style={fs.btnGray} onClick={onSchliessen}>Schließen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
